import { useState, useEffect, useRef, useCallback } from "react";

// Tiny stale-while-revalidate cache. Tabs render the last-known data INSTANTLY
// (no blank screens) while fresh data loads silently in the background.
// In-memory Map covers tab-to-tab navigation; sessionStorage survives full page
// reloads (cleared when the browser tab closes, and on logout).

const mem = new Map();
const SS_PREFIX = "rl_swr:";
const MAX_PERSIST = 400_000; // chars — stay well under the ~5MB sessionStorage quota

export function cacheGet(key) {
  if (mem.has(key)) return mem.get(key);
  try {
    const raw = sessionStorage.getItem(SS_PREFIX + key);
    if (raw != null) {
      const v = JSON.parse(raw);
      mem.set(key, v);
      return v;
    }
  } catch { /* ignore */ }
  return undefined;
}

export function cacheSet(key, value) {
  mem.set(key, value);
  try {
    const raw = JSON.stringify(value);
    if (raw.length <= MAX_PERSIST) sessionStorage.setItem(SS_PREFIX + key, raw);
    else sessionStorage.removeItem(SS_PREFIX + key);
  } catch { /* quota exceeded — the in-memory copy still works */ }
}

export function cacheClear() {
  mem.clear();
  try {
    Object.keys(sessionStorage)
      .filter((k) => k.startsWith(SS_PREFIX))
      .forEach((k) => sessionStorage.removeItem(k));
  } catch { /* ignore */ }
}

// Drop-in useState replacement that hydrates from the cache and writes every
// update back. Revisiting a tab shows the last-known data immediately while the
// page's normal load() refreshes it in the background.
export function useCachedState(key, initial) {
  const [data, setData] = useState(() => {
    const c = cacheGet(key);
    return c === undefined ? initial : c;
  });
  const keyRef = useRef(key);
  const initialRef = useRef(initial);
  useEffect(() => {
    if (keyRef.current !== key) {
      keyRef.current = key;
      const c = cacheGet(key);
      setData(c === undefined ? initialRef.current : c);
    }
  }, [key]);
  const set = useCallback((v) => {
    setData((prev) => {
      const next = typeof v === "function" ? v(prev) : v;
      cacheSet(key, next);
      return next;
    });
  }, [key]);
  return [data, set];
}
